import { useState } from 'react';
import type { ParsedRosterEntry } from '../../types';
import { getAirline } from '../../utils/airlines';
import { getCityName } from '../../utils/airports';
import { formatDateShort } from '../../utils/dates';
import { Plane, Coffee, Clock, Check, Loader2, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { hapticMedium } from '../../utils/haptics';

interface RosterResultsProps {
  entries: ParsedRosterEntry[];
  selectedIndices: Set<number>;
  selectedFlightCount: number;
  onToggle: (index: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onUpdateEntry: (index: number, updates: Partial<ParsedRosterEntry>) => void;
  onRemoveEntry: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}

export function RosterResults({
  entries,
  selectedIndices,
  selectedFlightCount,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onUpdateEntry,
  onRemoveEntry,
  onSave,
  onCancel,
  saving,
}: RosterResultsProps) {
  const flights = entries.filter((e) => e.type === 'flight');
  const flightCount = flights.length;
  const missingTimesCount = flights.filter((e) => !e.departureTime || !e.arrivalTime).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          Found <span className="font-semibold text-slate-200">{entries.length}</span> entries
          ({flightCount} flights)
        </p>
        <div className="flex gap-2">
          <button onClick={onSelectAll} className="text-xs text-accent font-medium">
            Select all
          </button>
          <button onClick={onDeselectAll} className="text-xs text-slate-500 font-medium">
            None
          </button>
        </div>
      </div>

      {missingTimesCount > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">
            {missingTimesCount} flight{missingTimesCount !== 1 ? 's' : ''} missing times — tap{' '}
            <Pencil size={10} className="inline text-amber-400" /> to add them for better meetup suggestions.
          </p>
        </div>
      )}

      <div
        className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          /* GPU compositing prevents black screen when scrolling large lists */
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
        }}
      >
        {entries.map((entry, i) => (
          <EntryRow
            key={i}
            entry={entry}
            index={i}
            selected={selectedIndices.has(i)}
            onToggle={onToggle}
            onUpdate={onUpdateEntry}
            onRemove={onRemoveEntry}
          />
        ))}
      </div>

      <div className="flex gap-2 mt-1">
        <button
          onClick={() => { hapticMedium(); onSave(); }}
          disabled={saving || selectedFlightCount === 0}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check size={16} />
              Save {selectedFlightCount} flight{selectedFlightCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  index,
  selected,
  onToggle,
  onUpdate,
  onRemove,
}: {
  entry: ParsedRosterEntry;
  index: number;
  selected: boolean;
  onToggle: (i: number) => void;
  onUpdate: (i: number, updates: Partial<ParsedRosterEntry>) => void;
  onRemove: (i: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const isFlight = entry.type === 'flight';
  const airline = entry.airlineCode ? getAirline(entry.airlineCode) : null;

  if (editing && isFlight) {
    return (
      <EditRow
        entry={entry}
        onSave={(updates) => {
          onUpdate(index, updates);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors ${
        isFlight
          ? selected
            ? 'bg-accent/15 border border-accent/30'
            : 'bg-slate-700/50 border border-slate-600/50 hover:bg-slate-700'
          : 'bg-slate-700/30 border border-transparent opacity-60'
      }`}
    >
      {/* Checkbox for flights */}
      {isFlight ? (
        <button
          onClick={() => onToggle(index)}
          className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
            selected
              ? 'bg-accent border-accent'
              : 'border-slate-500'
          }`}
        >
          {selected && <Check size={12} className="text-white" />}
        </button>
      ) : (
        <div className="w-5 h-5 shrink-0" />
      )}

      {/* Icon */}
      {entry.type === 'flight' && (
        <Plane size={14} className="text-accent shrink-0" />
      )}
      {entry.type === 'off' && (
        <Coffee size={14} className="text-sage shrink-0" />
      )}
      {entry.type === 'standby' && (
        <Clock size={14} className="text-amber-400 shrink-0" />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {entry.type === 'flight' && (
          <div className="flex items-center gap-1.5">
            {entry.flightNumber ? (
              <span className="text-sm font-semibold text-slate-100 font-mono">
                {entry.flightNumber}
              </span>
            ) : (
              <span className="text-sm italic text-slate-400">Route only</span>
            )}
            {airline && (
              <span
                className="text-[10px] px-1 py-0.5 rounded-full font-semibold"
                style={{ backgroundColor: airline.color, color: airline.textOnColor }}
              >
                {airline.name}
              </span>
            )}
          </div>
        )}
        {entry.type === 'flight' && entry.departureAirport && (
          <div className="text-xs text-slate-400 mt-0.5">
            <span className="font-mono">{entry.departureAirport}</span>
            {entry.departureAirport && getCityName(entry.departureAirport) !== entry.departureAirport &&
              ` ${getCityName(entry.departureAirport)}`}
            {' → '}
            <span className="font-mono">{entry.arrivalAirport}</span>
            {entry.arrivalAirport && getCityName(entry.arrivalAirport) !== entry.arrivalAirport &&
              ` ${getCityName(entry.arrivalAirport)}`}
            {entry.departureTime && <span className="font-mono"> · {entry.departureTime}</span>}
          </div>
        )}
        {entry.type === 'off' && (
          <span className="text-xs text-sage font-medium">
            Day off
            {entry.endDate && entry.endDate !== entry.date &&
              ` (${formatDateShort(entry.date)} — ${formatDateShort(entry.endDate)})`}
          </span>
        )}
        {entry.type === 'standby' && (
          <span className="text-xs text-amber-400 font-medium">
            Standby {entry.note && `· ${entry.note}`}
          </span>
        )}
      </div>

      {/* Missing times indicator */}
      {isFlight && (!entry.departureTime || !entry.arrivalTime) && (
        <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" title="Missing times" />
      )}

      {/* Date */}
      <span className="text-xs text-slate-500 font-mono shrink-0">
        {formatDateShort(entry.date)}
      </span>

      {/* Edit/Remove buttons for flights */}
      {isFlight && (
        <div className="flex gap-1 shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="p-1 rounded text-slate-500 hover:text-accent transition-colors"
            title="Edit"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            className="p-1 rounded text-slate-500 hover:text-red-400 transition-colors"
            title="Remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function EditRow({
  entry,
  onSave,
  onCancel,
}: {
  entry: ParsedRosterEntry;
  onSave: (updates: Partial<ParsedRosterEntry>) => void;
  onCancel: () => void;
}) {
  const [flightNumber, setFlightNumber] = useState(entry.flightNumber || '');
  const [dep, setDep] = useState(entry.departureAirport || '');
  const [arr, setArr] = useState(entry.arrivalAirport || '');
  const [date, setDate] = useState(entry.date);
  const [depTime, setDepTime] = useState(entry.departureTime || '');
  const [arrTime, setArrTime] = useState(entry.arrivalTime || '');

  return (
    <div className="p-3 rounded-lg border-2 border-accent/40 bg-slate-700/70">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Flight #</label>
          <input
            value={flightNumber}
            onChange={(e) => setFlightNumber(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100 font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">From</label>
          <input
            value={dep}
            onChange={(e) => setDep(e.target.value.toUpperCase())}
            maxLength={4}
            placeholder="IATA"
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100 font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">To</label>
          <input
            value={arr}
            onChange={(e) => setArr(e.target.value.toUpperCase())}
            maxLength={4}
            placeholder="IATA"
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100 font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Dep time</label>
          <input
            type="time"
            value={depTime}
            onChange={(e) => setDepTime(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Arr time</label>
          <input
            type="time"
            value={arrTime}
            onChange={(e) => setArrTime(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-slate-600 border border-slate-500 rounded text-slate-100"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-2.5">
        <button
          onClick={() => onSave({
            flightNumber: flightNumber.trim() || undefined,
            departureAirport: dep.trim() || undefined,
            arrivalAirport: arr.trim() || undefined,
            date,
            departureTime: depTime || undefined,
            arrivalTime: arrTime || undefined,
          })}
          className="flex-1 py-1.5 text-xs font-medium bg-accent text-white rounded-lg hover:bg-accent-dark"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
